import type { OrganisationMember } from "@/api/organisationApi";
import type { Member } from "@/types/Member";
import type { MessageableMember } from "@/types/MessageableEntity";

/**
 * The id of the organisation this member OWNS, if any. Prefer the explicit owner flag
 * (`ownerOfOrganisationId`), then a populated organisation object, then plain membership.
 */
export const resolveOwnedOrgId = (member: Member | null): string | null => {
  if (!member) return null;
  return member.ownerOfOrganisationId ?? member.organisation?.id ?? member.organisationId ?? null;
};

/**
 * Map an owner's org roster to MessageableMembers, dropping inactive members and filtering by a
 * free-text term (matched against name + email). This is the ONLY source of members an org owner
 * can pick from — it can never surface a non-org member.
 */
export const rosterToMessageableMembers = (
  members: OrganisationMember[],
  searchTerm: string,
): MessageableMember[] => {
  const term = searchTerm.trim().toLowerCase();
  return members
    .filter((m) => m.active)
    .filter((m) => {
      if (!term) return true;
      return `${m.firstName} ${m.lastName} ${m.email}`.toLowerCase().includes(term);
    })
    .map((m) => ({
      type: "member" as const,
      id: m.id,
      firstName: m.firstName,
      lastName: m.lastName,
      email: m.email,
      avatar: m.avatar,
      role: m.role,
      active: m.active,
    }));
};
